import React, { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Box,
  Chip,
  CircularProgress,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Stack,
  Tooltip,
} from '@mui/material';
import { useTheme } from "@mui/material";
import { useEventsApi } from '../../hooks/useEventsApi';
import {
  NumberRangeBaseFunction,
  descriptionTooltipText,
  numberRangeDisplayName,
} from '../../shared/NumberRange';
import {
  AGV_CONFIGURATOR_IMAGE,
  AGV_OVERLAY_ASPECT_RATIO,
  AGV_OVERLAY_MAX_WIDTH_PX,
  resolveChipPercent,
} from '../../shared/numberRangeChipLayout';
import { config } from "../../config";
import { getNumberRangeBaseFunctions } from "../../shared/numberRangeBasesCache";
import {
  getMissingCategoryLookupMessage,
  isResolvingBandCode,
  risingToResolvingCode,
} from "../../shared/eventCodeRange";

function parseApiDetail(err: unknown): string {
  if (err && typeof err === "object" && "responseBody" in err) {
    const raw = (err as { responseBody?: string }).responseBody;
    if (typeof raw === "string") {
      try {
        const j = JSON.parse(raw) as { detail?: string };
        if (typeof j.detail === "string") return j.detail;
      } catch {
        /* ignore */
      }
    }
  }
  return "Request failed.";
}

interface Props {
  data: {
    selectedBaseFunctionId?: number;
  };
  onValidationChange: (valid: boolean) => void;
  onDataChange: (data: any) => void;
  onAutoContinue: () => void;
  enteredByUser: boolean; // kann bleiben, wird hier aber nicht mehr fürs AutoContinue genutzt
  /** Optional: user has an existing rising code → skip base/sub chip selection and go to resolving-only flow. */
  onQuickResolvingFromCode?: (payload: { baseId: number; subId: number; risingCode: string }) => void;
  onOpenNumberRangeOverview?: () => void;
}

export default function Step1Form({
  data,
  onValidationChange,
  onDataChange,
  onAutoContinue,
  enteredByUser,
  onQuickResolvingFromCode,
  onOpenNumberRangeOverview,
}: Props) {

  const [selectedBaseFunction, setSelectedBaseFunction] = useState<number | null>(
    data.selectedBaseFunctionId ?? null
  );

  const { callApi } = useEventsApi();
  const callApiRef = useRef(callApi);
  callApiRef.current = callApi;
  const { accounts } = useMsal();
  const accountKey = accounts[0]?.homeAccountId ?? '';
  const theme = useTheme();

  const [loadingBase, setLoadingBase] = useState(true);
  const [baseFunctions, setBaseFunctions] = useState<NumberRangeBaseFunction[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [quickCodeInput, setQuickCodeInput] = useState("");
  const [quickError, setQuickError] = useState<string | null>(null);
  const [quickBusy, setQuickBusy] = useState(false);

  // ---------------------------------------------------
  // API laden
  // ---------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoadError(null);
      if (!accounts[0]) {
        setBaseFunctions([]);
        setLoadingBase(false);
        return;
      }
      const api = callApiRef.current;
      if (!api) {
        setBaseFunctions([]);
        setLoadingBase(false);
        return;
      }
      setLoadingBase(true);
      try {
        const raw = await getNumberRangeBaseFunctions((url, method) => api(url, method));
        if (cancelled) return;
        setBaseFunctions(raw);
      } catch (error) {
        console.error("Fehler beim Laden der BaseFunctions:", error);
        if (!cancelled) {
          setBaseFunctions([]);
          setLoadError(parseApiDetail(error));
        }
      } finally {
        if (!cancelled) {
          setLoadingBase(false);
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [accountKey]);

  // ---------------------------------------------------
  // Label (eng)
  // ---------------------------------------------------
  const getLabel = (id: number) => {
    const bf = baseFunctions.find((b) => b.id === id);
    if (!bf) return "...";
    return numberRangeDisplayName(bf.descriptions, "eng") || "...";
  };

  const sortedBases = [...baseFunctions].sort((a, b) => a.digit - b.digit || a.id - b.id);
  const visibleBases = sortedBases.filter(
    (b) => b.show_on_agv !== false && numberRangeDisplayName(b.descriptions, "eng") !== ""
  );

  // ---------------------------------------------------
  // Nur noch Sync (Validation/Data) via onClick – kein AutoContinue im Effect
  // ---------------------------------------------------
  // (Optional) Falls du externe Änderungen an data.selectedBaseFunctionId
  // in den lokalen State spiegeln willst, kannst du das hier tun:
  // useEffect(() => {
  //   if (data.selectedBaseFunctionId !== undefined) {
  //     setSelectedBaseFunction(data.selectedBaseFunctionId);
  //   }
  // }, [data.selectedBaseFunctionId]);

  // ---------------------------------------------------
  // Chip Stil
  // ---------------------------------------------------
  const handleQuickResolvingShortcut = async () => {
    if (!onQuickResolvingFromCode || !callApi) return;
    setQuickError(null);
    const n = parseInt(quickCodeInput.trim(), 10);
    if (Number.isNaN(n)) {
      setQuickError("Enter a numeric rising event code.");
      return;
    }
    if (isResolvingBandCode(n)) {
      setQuickError("Use the rising code (0xxxx), not the resolving code (1xxxx).");
      return;
    }
    setQuickBusy(true);
    try {
      const scope = await callApi(
        `${config.apiUrl}/api/number-range/resolve-event-code/?event_code=${n}`,
        "GET"
      );
      if (!scope?.base_id || !scope?.sub_id) {
        setQuickError(getMissingCategoryLookupMessage(n, []));
        return;
      }
      const evList = await callApi(
        `${config.apiUrl}/api/events/?event_code_exact=${n}&latest=true&page_size=5`,
        "GET"
      );
      const row = evList?.results?.[0];
      if (!row) {
        setQuickError(
          `No event with code ${n} was found. Create the rising event first, or check the code.`
        );
        return;
      }
      const full = await callApi(`${config.apiUrl}/api/events/${row.global_event_id}/`, "GET");
      if (full && isResolvingBandCode(full.event_code)) {
        setQuickError("This code is a resolving event. Enter the rising event code (0xxxx).");
        return;
      }
      const resolvingCode = risingToResolvingCode(Number(full.event_code));
      const dupRes = await callApi(
        `${config.apiUrl}/api/events/?event_code_exact=${resolvingCode}&latest=true&page_size=5`,
        "GET"
      );
      if (dupRes?.results?.length > 0) {
        const existingId = dupRes.results[0].global_event_id;
        setQuickError(
          `Resolving event ${resolvingCode} already exists (event id ${existingId}). No second resolving event is needed.`
        );
        return;
      }
      onQuickResolvingFromCode({
        baseId: scope.base_id,
        subId: scope.sub_id,
        risingCode: String(n),
      });
    } catch (e) {
      const status =
        e && typeof e === "object" && "status" in e
          ? (e as { status?: number }).status
          : undefined;
      if (status === 404) {
        setQuickError(getMissingCategoryLookupMessage(n, []));
      } else {
        setQuickError(parseApiDetail(e));
      }
    } finally {
      setQuickBusy(false);
    }
  };

  const chipStyles = (id: number) => ({
    backgroundColor:
      selectedBaseFunction === id
        ? theme.palette.primary.main
        : undefined,
    color:
      selectedBaseFunction === id
        ? theme.palette.primary.contrastText
        : undefined,
  });

  // ---------------------------------------------------
  // Loading
  // ---------------------------------------------------
  if (loadingBase) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "40vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // ---------------------------------------------------
  // RENDER
  // ---------------------------------------------------
  return (
    // Deliberately uncapped: the document is the scroll container (see AppContent), so a
    // height cap here would only let the content paint over whatever follows the page.
    <Box sx={{ p: 2 }}>
      <Typography
        variant="h6"
        sx={{ mb: 1, fontWeight: "bold", color: theme.palette.text.primary, textAlign: "center" }}
      >
        Please select base-function:
      </Typography>
      {onOpenNumberRangeOverview && (
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Button size="small" variant="text" onClick={onOpenNumberRangeOverview}>
            Not sure? Show all functions
          </Button>
        </Box>
      )}
      {loadError && (
        <Alert severity="error" sx={{ mb: 2, maxWidth: 800, mx: "auto" }}>
          {loadError}
        </Alert>
      )}

      <Box
        sx={{
          width: "100%",
          display: "grid",
          gap: 3,
          alignItems: "center",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(0, 800px) minmax(0, 1fr)" },
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            gridColumn: { xs: "1", lg: "2" },
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: AGV_OVERLAY_MAX_WIDTH_PX,
              mx: "auto",
              aspectRatio: AGV_OVERLAY_ASPECT_RATIO,
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Box
              component="img"
              src={AGV_CONFIGURATOR_IMAGE}
              alt="AGV"
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: "block",
                pointerEvents: "none",
              }}
            />

            {visibleBases.map((bf, index) => {
              const pos = resolveChipPercent(bf.chip_top, bf.chip_left, index, visibleBases.length);
              const tip = descriptionTooltipText(bf.descriptions, "eng");
              return (
                <Tooltip
                  key={bf.id}
                  title={tip}
                  placement="top"
                  enterDelay={350}
                  disableHoverListener={!tip}
                  slotProps={{
                    tooltip: { sx: { maxWidth: 380, whiteSpace: "pre-wrap" } },
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: pos.top,
                      left: pos.left,
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    <Chip
                      color="warning"
                      label={getLabel(bf.id)}
                      clickable
                      onClick={() => {
                        onValidationChange(true);
                        onDataChange({
                          selectedBaseFunctionId: bf.id,
                          selectedBaseFunctionName: getLabel(bf.id),
                          selectedBaseDigit: bf.digit,
                        });
                        setSelectedBaseFunction(bf.id);
                        onAutoContinue();
                      }}
                      sx={chipStyles(bf.id)}
                    />
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>

        {onQuickResolvingFromCode && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              width: { xs: "100%", lg: 280 },
              maxWidth: { xs: 800, lg: 460 },
              borderColor: theme.palette.divider,
              justifySelf: { xs: "stretch", lg: "end" },
              gridColumn: { xs: "1", lg: "3" },
              position: "relative",
              zIndex: 2,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 0.5 }}>
              Already have a rising event code?
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: 2, textAlign: "left" }}
            >
              Jump to <b>resolving-only</b> setup. Enter an existing rising event code.
            </Typography>
            <Stack
              direction="column"
              spacing={1.5}
              alignItems="stretch"
              flexWrap="wrap"
            >
              <TextField
                label="Rising event code"
                value={quickCodeInput}
                onChange={(e) => setQuickCodeInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !quickBusy) {
                    e.preventDefault();
                    void handleQuickResolvingShortcut();
                  }
                }}
                size="small"
                disabled={quickBusy}
                fullWidth
              />
              <Button
                size="small"
                variant="contained"
                color="secondary"
                disabled={quickBusy}
                fullWidth
                onClick={() => void handleQuickResolvingShortcut()}
              >
                {quickBusy ? <CircularProgress size={22} color="inherit" /> : "Continue with resolving event"}
              </Button>
            </Stack>
            {quickError && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                {quickError}
              </Typography>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
}